<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-[60] flex flex-col bg-black">
      <!-- 头部 -->
      <div class="flex items-center gap-3 px-5 py-3 bg-slate-900 text-white shrink-0 border-b border-slate-700">
        <FileIcon :file="file" class="w-8 h-8" />
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-white truncate">{{ fileName }}</div>
          <div class="text-xs text-slate-400 mt-0.5">
            <span class="mr-2">{{ fileSize }}</span>
            <span class="text-slate-400">{{ typeBadge }}</span>
          </div>
        </div>
          <button
            v-if="downloadable"
            class="px-3 py-1.5 text-sm text-slate-200 hover:text-white border border-slate-600 hover:border-slate-400 hover:bg-slate-700 rounded-lg flex items-center gap-1.5 shrink-0"
            :disabled="downloadLoading"
            @click="handleDownload"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"/>
            </svg>
            {{ downloadLoading ? '下载中' : '下载' }}
          </button>
          <button
            v-if="showPrev"
            class="p-1.5 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg shrink-0"
            title="上一个"
            @click="$emit('prev')"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <button
            v-if="showNext"
            class="p-1.5 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg shrink-0"
            title="下一个"
            @click="$emit('next')"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
          <button
            class="p-1.5 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg shrink-0"
            @click="close"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- 主体 -->
        <div :class="previewBodyClass">
          <div v-if="loading" class="absolute inset-0 flex items-center justify-center">
            <div class="flex flex-col items-center gap-3 text-slate-400">
              <svg class="w-8 h-8 animate-spin text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              <span class="text-sm">加载中…</span>
            </div>
          </div>

          <!-- 图片 -->
          <div v-if="strategy === 'image' && displayUrl" class="min-h-full flex items-center justify-center p-6">
            <img
              :src="displayUrl"
              :alt="fileName"
              class="max-w-full max-h-[calc(100vh-64px)] object-contain"
              @load="onLoad"
              @error="onError"
            />
          </div>

          <!-- PDF -->
          <div v-else-if="strategy === 'pdf' && displayUrl" class="w-full h-full" style="min-height: calc(100vh - 56px);">
            <component
              :is="pdfComp"
              :src="displayUrl"
              class="w-full h-full"
              @rendered="onLoad"
              @error="onError"
            />
          </div>

          <!-- docx -->
          <div v-else-if="strategy === 'docx' && displayUrl" class="w-full min-h-full bg-white">
            <component
              :is="docxComp"
              :src="displayUrl"
              class="w-full h-full"
              @rendered="onLoad"
              @error="onError"
            />
          </div>

          <!-- xlsx -->
          <div v-else-if="strategy === 'xlsx' && displayUrl" class="excel-preview-pane">
            <component
              :is="xlsxComp"
              :src="displayUrl"
              class="excel-preview-frame"
              @rendered="onLoad"
              @error="onError"
            />
          </div>

          <!-- Office Online -->
          <div v-else-if="strategy === 'office' && officeViewerUrl" class="office-preview-pane">
            <iframe
              :src="officeViewerUrl"
              class="office-preview-frame"
              title="Office 预览"
              allowfullscreen
              @load="onLoad"
              @error="onError"
            ></iframe>
          </div>

          <!-- 不可预览（兜底：download / office / video / audio 等） -->
          <div v-else class="absolute inset-0 flex items-center justify-center">
            <div class="text-center">
              <FileIcon :file="file" class="w-16 h-16 mx-auto mb-3" />
              <p class="text-sm text-slate-400 mb-4">此文件类型暂不支持在线预览</p>
              <button
                v-if="downloadable"
                class="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
                :disabled="downloadLoading"
                @click="handleDownload"
              >{{ downloadLoading ? '下载中' : '下载文件' }}</button>
            </div>
          </div>

          <!-- 加载错误 -->
          <div v-if="error" class="absolute inset-0 flex items-center justify-center bg-black/80">
            <div class="text-center">
              <svg class="w-12 h-12 mx-auto mb-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z"/>
              </svg>
              <p class="text-sm text-slate-300 mb-3">预览失败</p>
              <button
                v-if="downloadable"
                class="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
                :disabled="downloadLoading"
                @click="handleDownload"
              >{{ downloadLoading ? '下载中' : '下载文件查看' }}</button>
            </div>
          </div>
        </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, defineAsyncComponent, onBeforeUnmount, ref, watch } from 'vue'
import FileIcon from './FileIcon.vue'
import { getPreviewStrategy, getFileName, getFileSize, getFileUrl, getFileType } from '@/utils/fileType'
import '@vue-office/docx/lib/index.css'
import '@vue-office/excel/lib/index.css'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  file: { type: [Object, String], default: null },
  showPrev: { type: Boolean, default: false },
  showNext: { type: Boolean, default: false },
  loader: { type: Function, default: null }
})

const emit = defineEmits(['update:modelValue', 'prev', 'next'])

const pdfComp = defineAsyncComponent(() => import('@vue-office/pdf'))
const docxComp = defineAsyncComponent(() => import('@vue-office/docx'))
const xlsxComp = defineAsyncComponent(() => import('@vue-office/excel'))

const loading = ref(true)
const error = ref(false)
const downloadLoading = ref(false)
const loadedUrl = ref('')
const loadedObjectUrl = ref(false)
const loadedContentType = ref('')
let loadSeq = 0

const effectiveFile = computed(() => {
  if (!loadedContentType.value || !props.file || typeof props.file !== 'object') return props.file
  return { ...props.file, contentType: loadedContentType.value }
})
const strategy = computed(() => effectiveFile.value ? getPreviewStrategy(effectiveFile.value) : 'download')
const fileName = computed(() => getFileName(props.file))
const fileSize = computed(() => getFileSize(props.file))
const fileUrl = computed(() => getFileUrl(props.file))
const shouldLoadPreview = computed(() => Boolean(
  props.file &&
  props.loader &&
  typeof props.file === 'object' &&
  props.file.previewMode === 'blob'
))
const displayUrl = computed(() => shouldLoadPreview.value ? loadedUrl.value : loadedUrl.value || fileUrl.value)
const officeViewerUrl = computed(() => {
  if (strategy.value !== 'office' || !displayUrl.value) return ''
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(displayUrl.value)}`
})
const downloadable = computed(() => Boolean(getDownloadSource(props.file) || displayUrl.value))

const typeBadgeMap = {
  image: '图片', pdf: 'PDF', docx: 'Word', xlsx: 'Excel',
  doc: 'Word', xls: 'Excel', ppt: 'PPT', pptx: 'PPT',
  archive: '压缩包', video: '视频', audio: '音频', text: '文本', file: '文件'
}
const typeBadge = computed(() => {
  if (!props.file) return '文件'
  return typeBadgeMap[getFileType(effectiveFile.value)] || '文件'
})

const previewBodyClass = computed(() => {
  if (strategy.value === 'xlsx' || strategy.value === 'office') {
    return 'flex-1 overflow-hidden bg-white relative'
  }
  return 'flex-1 overflow-auto bg-black relative'
})

watch(() => props.modelValue, (v) => {
  if (v) {
    loadPreview()
  } else {
    clearLoadedUrl()
  }
})

watch(() => props.file, () => {
  if (props.modelValue) {
    loadPreview()
  }
})

function close() {
  emit('update:modelValue', false)
}

function onLoad() {
  loading.value = false
  error.value = false
}

function onError() {
  loading.value = false
  error.value = true
}

async function loadPreview() {
  const seq = ++loadSeq
  clearLoadedUrl()
  loadedContentType.value = ''
  error.value = false

  if (!shouldLoadPreview.value) {
    loading.value = strategy.value !== 'download'
    return
  }

  loading.value = true
  try {
    const result = await props.loader(props.file, { purpose: 'preview' })
    if (seq !== loadSeq) return
    setLoadedUrl(result)
  } catch {
    if (seq === loadSeq) {
      error.value = true
      loading.value = false
    }
  }
}

function setLoadedUrl(result) {
  clearLoadedUrl()
  const payload = result?.data ?? result
  const contentType = result?.contentType || result?.headers?.['content-type'] || payload?.type || props.file?.contentType
  loadedContentType.value = contentType || ''
  if (typeof payload === 'string') {
    loadedUrl.value = payload
    loadedObjectUrl.value = false
  } else if (payload instanceof Blob) {
    loadedUrl.value = URL.createObjectURL(payload)
    loadedObjectUrl.value = true
  } else {
    loadedUrl.value = fileUrl.value
    loadedObjectUrl.value = false
  }
  loading.value = strategy.value !== 'download'
}

function clearLoadedUrl() {
  if (loadedObjectUrl.value && loadedUrl.value) {
    URL.revokeObjectURL(loadedUrl.value)
  }
  loadedUrl.value = ''
  loadedObjectUrl.value = false
  loadedContentType.value = ''
}

async function handleDownload() {
  if (props.loader && typeof props.file === 'object' && props.file.previewMode === 'blob') {
    await downloadWithLoader()
    return
  }
  const url = getDownloadSource(props.file) || displayUrl.value
  if (!url) return
  const a = document.createElement('a')
  a.href = url
  a.download = fileName.value || ''
  a.target = '_blank'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

async function downloadWithLoader() {
  if (downloadLoading.value) return
  downloadLoading.value = true
  try {
    const result = await props.loader(props.file, { purpose: 'download' })
    const payload = result?.data ?? result
    if (payload instanceof Blob) {
      downloadBlob(payload, fileName.value)
      return
    }
    const url = typeof payload === 'string' ? payload : getDownloadSource(props.file) || displayUrl.value
    if (url) downloadUrl(url, fileName.value)
  } finally {
    downloadLoading.value = false
  }
}

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob)
  downloadUrl(url, name)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function downloadUrl(url, name) {
  const a = document.createElement('a')
  a.href = url
  a.download = name || ''
  a.target = '_blank'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

function getDownloadSource(file) {
  if (!file || typeof file === 'string') return ''
  return file.downloadSource || file.downloadUrl || ''
}

onBeforeUnmount(() => {
  loadSeq++
  clearLoadedUrl()
})
</script>

<style scoped>
.excel-preview-pane {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #fff;
}

.excel-preview-frame {
  display: block;
  width: 100%;
  height: 100%;
  background: #fff;
}

.excel-preview-pane :deep(.x-spreadsheet) {
  width: 100% !important;
  height: 100% !important;
  background: #fff;
}

.excel-preview-pane :deep(.x-spreadsheet-sheet) {
  height: 100% !important;
  background: #fff;
}

.office-preview-pane {
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 56px);
  overflow: hidden;
  background: #fff;
}

.office-preview-frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}
</style>
